import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Users, Building2, DollarSign, Layers, ScrollText } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { fmtCurrency, fmtDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

function Metric({ icon: Icon, label, value, accent, i }) {
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
      <Card className="p-6 rounded-sm border-border">
        <div className="flex items-center justify-between mb-4">
          <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
          <Icon className="h-4 w-4" style={{ color: accent }} />
        </div>
        <p className="font-display text-3xl tabular-nums">{value}</p>
      </Card>
    </motion.div>
  );
}

export default function Admin() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const { data: metrics } = useQuery({ queryKey: ["admin-metrics"], queryFn: async () => (await api.get("/admin/metrics")).data });
  const { data: users = [] } = useQuery({ queryKey: ["admin-users"], queryFn: async () => (await api.get("/admin/users")).data });
  const { data: logs = [] } = useQuery({ queryKey: ["admin-logs"], queryFn: async () => (await api.get("/admin/audit-logs?limit=50")).data });

  const setPlan = useMutation({
    mutationFn: ({ id, plan }) => api.put(`/admin/users/${id}/plan?plan=${plan}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-users"] }); qc.invalidateQueries({ queryKey: ["admin-metrics"] }); toast.success("OK"); },
  });

  return (
    <div className="space-y-6" data-testid="admin-page">
      <div>
        <h1 className="font-display text-4xl">{t("admin_title")}</h1>
        <p className="text-muted-foreground text-sm mt-2">YieldPilot · Super Admin</p>
      </div>

      {metrics && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Metric i={0} icon={Users} label={t("total_users")} value={metrics.total_users} accent="#10B981" />
          <Metric i={1} icon={Building2} label={t("properties")} value={metrics.total_properties} accent="#3B82F6" />
          <Metric i={2} icon={DollarSign} label={t("mrr")} value={fmtCurrency(metrics.mrr)} accent="#F59E0B" />
          <Metric i={3} icon={Layers} label={t("managed_income")} value={fmtCurrency(metrics.managed_income)} accent="#8B5CF6" />
        </div>
      )}

      <Card className="rounded-sm border-border overflow-hidden">
        <div className="px-6 py-4 border-b border-border">
          <h3 className="text-sm uppercase tracking-wider text-muted-foreground">{t("users")}</h3>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("name")}</TableHead>
              <TableHead>{t("email")}</TableHead>
              <TableHead>{t("properties")}</TableHead>
              <TableHead>{t("plan")}</TableHead>
              <TableHead>{t("uploaded")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} data-testid={`admin-user-${u.id}`}>
                <TableCell className="font-medium">
                  {u.name} {u.role === "admin" && <Badge className="rounded-sm ml-1 bg-[hsl(var(--warning))] text-black">admin</Badge>}
                </TableCell>
                <TableCell className="text-muted-foreground">{u.email}</TableCell>
                <TableCell className="tabular-nums">{u.properties}</TableCell>
                <TableCell>
                  <Select value={u.plan} onValueChange={(plan) => setPlan.mutate({ id: u.id, plan })}>
                    <SelectTrigger className="rounded-sm w-32 h-8" data-testid={`plan-select-${u.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-popover">
                      <SelectItem value="basic">basic</SelectItem>
                      <SelectItem value="pro">pro</SelectItem>
                      <SelectItem value="business">business</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="text-muted-foreground">{fmtDate(u.created_at)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Card className="rounded-sm border-border overflow-hidden" data-testid="audit-logs">
        <div className="px-6 py-4 border-b border-border flex items-center gap-2">
          <ScrollText className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm uppercase tracking-wider text-muted-foreground">Audit logs</h3>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Action</TableHead>
              <TableHead>{t("email")}</TableHead>
              <TableHead>IP</TableHead>
              <TableHead>{t("uploaded")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {logs.map((l) => (
              <TableRow key={l.id} data-testid={`log-${l.id}`}>
                <TableCell><Badge variant="secondary" className="rounded-sm font-mono text-xs">{l.action}</Badge></TableCell>
                <TableCell className="text-muted-foreground">{l.user_email}</TableCell>
                <TableCell className="text-muted-foreground font-mono text-xs">{l.ip || "—"}</TableCell>
                <TableCell className="text-muted-foreground">{fmtDate(l.created_at)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
